// Admin → Device Tracking. The estate as three questions — which live devices
// nobody has been given, which assigned ones have gone silent and for how
// long, which live ones are running old firmware — from ONE server reply.
// Super Admin only: the third view reflashes hardware. Both shells route here.
//
// The questions, the filters and every count live in core/admin/deviceTracking
// (pure); this file is the dropdowns, the table, and the detail panel with the
// device's live controls — the same DeviceControlCard as the room grid, so a
// press publishes and the colour moves only when the device echoes.
//
// ⚠️ THE LOAD IS ONE REQUEST (GetDeviceTrackingList, ~1800 rows, ~0.5 s) and
// needs the backend that carries it. An older server answers with an HTML 404
// and the page says "not deployed" — it never falls back to walking 1800
// per-device calls.

import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, AppBar, Toolbar, IconButton, Button, TextField, MenuItem,
  Alert, Chip, Checkbox, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, LinearProgress, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import CloseIcon from '@mui/icons-material/Close';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import BookmarkAddIcon from '@mui/icons-material/BookmarkAdd';
import { useApp } from '../../../core/state/AppContext';
import {
  VIEWS, VIEW_OFFLINE, OFFLINE_DAYS, NOT_DEPLOYED,
  loadTracking, rowsForView, applyFilters, deviceTypeOptions, locationOptions, trackingCounts,
  loadTypeChoices, loadBinChoices, setTypeForDevices, setBinForDevices, planUnassign, runUnassign,
} from '../../../core/admin/deviceTracking';
import { sendFirmwareUpdate, markFirmwareUpdates, previewPayload } from '../../../core/devices/firmwareUpdate';
import { deviceTypeCaption, deviceCardWidth } from '../../../core/devices/deviceMeta';
import { CURRENT, STALE } from '../../../core/devices/firmwareInfo';
import DeviceControlCard from '../components/DeviceControlCard';
import { FIT_SM } from '../../shared/fitDialog';

const when = (ms) => (ms == null ? '—'
  : new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }));

const days = (n) => (n == null ? 'never' : `${n} d`);

const FW_CHIP = {
  [STALE]: { label: 'Needs update', color: 'warning' },
  [CURRENT]: { label: 'Up to date', color: 'success' },
  unknown: { label: 'Not known', color: 'default' },
};

// Device type and bin file, each with its OWN Save — the two install-row
// columns that are most often wrong, corrected where the mistake is seen.
// `unos` is one device (the detail panel) or every checked row (the bulk bar);
// the control is the same so the two cannot drift apart.
//
// Save is enabled only when the choice differs from what the row(s) carry, and
// a bulk save with mixed current values shows "(mixed)" so the operator knows
// they are overwriting more than one thing.
function TypeBinEditor({
  unos, currentTypeId, currentBinId, types, bins, onSaved, disabled, dense,
}) {
  const [typeId, setTypeId] = useState('');
  const [binId, setBinId] = useState('');
  const [busy, setBusy] = useState('');     // 'type' | 'bin' | ''
  const [note, setNote] = useState(null);   // { severity, text }

  // Reset the pickers whenever the target changes, so a choice made for one
  // device is not carried, unsaved, onto the next one opened.
  const key = unos.join(',');
  useEffect(() => {
    setTypeId(currentTypeId == null ? '' : String(currentTypeId));
    setBinId(currentBinId == null ? '' : String(currentBinId));
    setNote(null);
  }, [key, currentTypeId, currentBinId]);

  const typeChanged = typeId !== '' && String(typeId) !== String(currentTypeId ?? '');
  const binChanged = binId !== '' && String(binId) !== String(currentBinId ?? '');
  const n = unos.length;
  const many = n > 1;

  const saveType = async () => {
    setBusy('type'); setNote(null);
    const out = await setTypeForDevices(unos, typeId);
    setBusy('');
    setNote(out.failed.length
      ? { severity: 'error', text: `Type saved on ${out.done}, failed on ${out.failed.length}: ${out.failed.join(', ')}` }
      : { severity: 'success', text: `Type saved on ${out.done} device${out.done === 1 ? '' : 's'}.` });
    onSaved();
  };

  const saveBin = async () => {
    const bin = bins.find((b) => String(b.otaBinFileId) === String(binId));
    setBusy('bin'); setNote(null);
    const out = await setBinForDevices(unos, binId, bin?.binFileName);
    setBusy('');
    // "failed" here includes a write that took but is not what the device is
    // served — mapDeviceToBinFile reads the OTA link back, and that read is the
    // point of doing it per device.
    setNote(out.failed.length
      ? { severity: 'error', text: `Bin file verified on ${out.done}, NOT served on ${out.failed.length}: ${out.failed.join(', ')}` }
      : { severity: 'success', text: `Bin file saved and verified on ${out.done} device${out.done === 1 ? '' : 's'}.` });
    onSaved();
  };

  const w = dense ? 220 : 280;
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          select size="small" label={many && currentTypeId === MIXED ? 'Device type (mixed)' : 'Device type'}
          value={typeId} onChange={(e) => setTypeId(e.target.value)}
          disabled={disabled || !!busy || !types.length}
          sx={{ width: { xs: '100%', sm: w } }}
        >
          {types.map((t) => <MenuItem key={t.deviceId} value={String(t.deviceId)}>{t.label}</MenuItem>)}
        </TextField>
        <Button
          size="small" variant="contained"
          disabled={disabled || !typeChanged || !!busy}
          onClick={saveType}
        >
          {busy === 'type' ? 'Saving…' : `Save type${many ? ` to ${n}` : ''}`}
        </Button>
        <TextField
          select size="small" label={many && currentBinId === MIXED ? 'Bin file (mixed)' : 'Bin file'}
          value={binId} onChange={(e) => setBinId(e.target.value)}
          disabled={disabled || !!busy || !bins.length}
          sx={{ width: { xs: '100%', sm: w } }}
        >
          {bins.map((b) => <MenuItem key={b.otaBinFileId} value={String(b.otaBinFileId)}>{b.binFileName}</MenuItem>)}
        </TextField>
        <Button
          size="small" variant="contained"
          disabled={disabled || !binChanged || !!busy}
          onClick={saveBin}
        >
          {busy === 'bin' ? 'Saving…' : `Save bin${many ? ` to ${n}` : ''}`}
        </Button>
      </Box>
      {note && <Alert severity={note.severity} sx={{ mt: 1 }} onClose={() => setNote(null)}>{note.text}</Alert>}
    </Box>
  );
}

// Sentinel for "the checked rows do not all carry the same value".
const MIXED = '__mixed__';
const commonValue = (rows, pick) => {
  const vals = new Set(rows.map((r) => String(pick(r) ?? '')));
  if (vals.size === 0) return null;
  return vals.size === 1 ? [...vals][0] : MIXED;
};

function Fact({ k, v }) {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', minWidth: 110 }}>{k}</Typography>
      <Typography sx={{ fontSize: 13, fontWeight: 600, wordBreak: 'break-all' }}>{v ?? '—'}</Typography>
    </Box>
  );
}

// The selected device: its facts, then its working buttons.
function DetailPanel({
  row, onClose, types, bins, onSaved, onUpdate, onMark,
}) {
  const fw = FW_CHIP[row.firmwareState] || FW_CHIP.unknown;
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, flex: 1 }}>{row.deviceName}</Typography>
        <Chip size="small" label={row.isLive ? 'Online' : 'Offline'} color={row.isLive ? 'success' : 'default'} sx={{ mr: 1 }} />
        {row.isLive && (
          <Button
            size="small" variant="contained" color="warning" startIcon={<SystemUpdateAltIcon />}
            sx={{ mr: 1 }} onClick={onUpdate}
          >
            Update code
          </Button>
        )}
        <Button
          size="small" variant="outlined" color="warning" startIcon={<BookmarkAddIcon />}
          sx={{ mr: 1 }} onClick={onMark} disabled={row.updateMarked}
        >
          {row.updateMarked ? 'Marked' : 'Mark'}
        </Button>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 4, rowGap: 0.25, mb: 1.5 }}>
        <Box>
          <Fact k="Type" v={deviceTypeCaption(row.raw)} />
          <Fact k="Unique number" v={row.uno} />
          <Fact k="Location" v={row.locationName} />
          <Fact k="Room" v={row.roomName} />
          <Fact k="Owner" v={row.ownerName || (row.masterUserId ? `User ${row.masterUserId}` : '—')} />
          <Fact k="Users mapped" v={row.mappedUsers} />
        </Box>
        <Box>
          <Fact k="Last heard" v={row.lastSeen == null ? 'never' : `${when(row.lastSeen)} (${days(row.offlineDays)} ago)`} />
          <Fact k="Last status" v={row.raw.Status || '—'} />
          <Fact k="Firmware" v={`${fw.label}${row.updateMarked ? ' · marked for update' : ''}`} />
          <Fact k="Bin file" v={row.binFile || '—'} />
          <Fact k="Built" v={when(row.builtAt)} />
          <Fact k="Last code updated" v={when(row.flashedAt)} />
        </Box>
      </Box>
      {/* Correct the two facts above, in place. The card below is drawn from
          DeviceID, so a wrong type is visible right here — and fixable. */}
      <Box sx={{ mb: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>Correct this device</Typography>
        <TypeBinEditor
          unos={[row.uno]}
          currentTypeId={row.deviceId}
          currentBinId={row.raw.OTABinFileId}
          types={types}
          bins={bins}
          onSaved={onSaved}
        />
      </Box>
      <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
        Live controls — a press publishes; the button changes only when the device answers.
      </Typography>
      <Box sx={{ flex: `0 1 ${deviceCardWidth(row.deviceId)}px`, maxWidth: '100%', width: deviceCardWidth(row.deviceId) }}>
        <DeviceControlCard device={row.raw} />
      </Box>
    </Paper>
  );
}

export default function DeviceTrackingScreen() {
  const { isSuperAdmin, userId } = useApp();
  const navigate = useNavigate();

  const [phase, setPhase] = useState('idle');   // idle | loading | ready | sending | done
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [notDeployed, setNotDeployed] = useState(false);

  const [view, setView] = useState(VIEWS[0].key);
  const [offlineDays, setOfflineDays] = useState(60);
  const [deviceId, setDeviceId] = useState('');
  const [locationId, setLocationId] = useState('');
  const [search, setSearch] = useState('');

  const [selected, setSelected] = useState(() => new Set());
  const [picked, setPicked] = useState(null);      // uno of the row opened below
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [targets, setTargets] = useState([]);       // what the update dialog acts on
  const [mode, setMode] = useState('send');         // 'send' = MQTT now · 'mark' = when it next connects
  const [unassignOpen, setUnassignOpen] = useState(false);
  const [unassignPlan, setUnassignPlan] = useState(null);   // null while planning
  const [unassignResult, setUnassignResult] = useState(null);
  const [progress, setProgress] = useState(null);
  const [result, setResult] = useState(null);
  const [typeChoices, setTypeChoices] = useState([]);   // the whole catalog
  const [binChoices, setBinChoices] = useState([]);     // every recorded build

  // The two correction lists — once, and small. Failure leaves the pickers
  // empty (and their Saves disabled) rather than taking the page down.
  useEffect(() => {
    loadTypeChoices().then(setTypeChoices).catch(() => setTypeChoices([]));
    loadBinChoices().then(setBinChoices).catch(() => setBinChoices([]));
  }, []);

  // `keep` — a re-read after a save: the estate is re-fetched so the row shows
  // the new type / bin (and the firmware verdict that follows from the bin's
  // date), but the opened row and the ticks survive, since the operator is
  // mid-task on exactly those devices.
  const load = useCallback(async ({ keep = false } = {}) => {
    setPhase('loading'); setError(''); setNotDeployed(false);
    if (!keep) { setSelected(new Set()); setPicked(null); setResult(null); }
    try {
      const out = await loadTracking();
      if (out.error === NOT_DEPLOYED) { setNotDeployed(true); setRows([]); setPhase('idle'); return; }
      setRows(out.rows);
      setPhase('ready');
    } catch (e) {
      setError('The device list could not be read.');
      setPhase('idle');
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  const reloadKeeping = useCallback(() => load({ keep: true }), [load]);

  // The view's rows, then the operator's filters over them. The type and
  // location dropdowns list what is in the VIEW, not the whole estate, so a
  // choice always has rows behind it.
  const viewRows = useMemo(() => rowsForView(rows, view, offlineDays), [rows, view, offlineDays]);
  const types = useMemo(() => deviceTypeOptions(viewRows), [viewRows]);
  const locations = useMemo(() => locationOptions(viewRows), [viewRows]);
  const visible = useMemo(
    () => applyFilters(viewRows, { deviceId, locationId, search }),
    [viewRows, deviceId, locationId, search],
  );
  const counts = useMemo(() => trackingCounts(rows), [rows]);

  // A filter that no longer has rows behind it is dropped, not left pointing
  // at nothing — switching view with "4-Gang" chosen must not show an empty
  // table for a view full of 4-gangs at other locations.
  useEffect(() => {
    if (deviceId !== '' && !types.some((t) => String(t.deviceId) === String(deviceId))) setDeviceId('');
  }, [types, deviceId]);
  useEffect(() => {
    if (locationId !== '' && !locations.some((l) => String(l.locationId) === String(locationId))) setLocationId('');
  }, [locations, locationId]);

  // Checkboxes are in EVERY view: a wrong type or bin file is found on an
  // unassigned or silent device as often as on one due for update.
  const onScreen = useMemo(() => new Set(visible.map((r) => r.uno)), [visible]);
  const chosen = useMemo(
    () => visible.filter((r) => selected.has(r.uno)),
    [visible, selected],
  );
  const chosenLive = useMemo(() => chosen.filter((r) => r.isLive), [chosen]);
  const allSelected = visible.length > 0 && visible.every((r) => selected.has(r.uno));
  const someSelected = visible.some((r) => selected.has(r.uno));

  const toggle = (uno) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(uno)) next.delete(uno); else next.add(uno);
    return next;
  });
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allSelected) onScreen.forEach((u) => next.delete(u));
    else onScreen.forEach((u) => next.add(u));
    return next;
  });

  // ── Two different instructions, kept apart on purpose ───────────────────
  //   send  — publish `updatecode` over MQTT NOW. The device flashes at once if
  //           it is listening; nothing is written on the server, so a device
  //           that missed it is not told again.
  //   mark  — RequireUpdateCode = 1 (+ the parked PendingCommand) on the
  //           server. Nothing goes over the air; the device flashes when it
  //           next connects / next polls. For devices that are offline now.
  // The Firmware page's "Update" does both at once; here the operator asked
  // for them separately, so each button does exactly one thing and says so.
  const askUpdate = (list, which) => {
    if (!list.length) return;
    setTargets(list);
    setMode(which);
    setConfirmOpen(true);
  };

  const run = async () => {
    setConfirmOpen(false);
    const list = targets;
    if (!list.length) return;
    setPhase('sending');
    if (mode === 'send') {
      setProgress({ sent: 0, total: list.length });
      const sent = await sendFirmwareUpdate(list, userId, (n) => setProgress({ sent: n, total: list.length }));
      setResult({ mode, sent, total: list.length });
    } else {
      setProgress({ sent: 0, total: list.length, label: 'Marking' });
      const { marked, failed } = await markFirmwareUpdates(list);
      setResult({ mode, marked, failed, total: list.length });
    }
    setProgress(null);
    setSelected(new Set());
    setTargets([]);
    setPhase('ready');
  };

  // Return the checked devices to stock — see planUnassign / runUnassign for
  // what that means, what follows (empty rooms and locations), and why it is
  // not reversible from here. The plan is read while the dialog is open so
  // the operator confirms what will ACTUALLY happen, not a description of it.
  const openUnassign = async () => {
    if (!chosen.length) return;
    setUnassignPlan(null);
    setUnassignOpen(true);
    const plan = await planUnassign(chosen).catch(() => ({ error: true }));
    setUnassignPlan(plan);
  };
  const unassign = async () => {
    const plan = unassignPlan;
    setUnassignOpen(false);
    if (!plan || plan.error) return;
    setPhase('sending');
    setProgress({ sent: 0, total: plan.steps.length, label: 'Unassigning' });
    const out = await runUnassign(plan);
    setUnassignResult(out);
    setProgress(null);
    setSelected(new Set());
    await load({ keep: true });
  };

  if (!isSuperAdmin) {
    return <Box sx={{ p: 3 }}><Alert severity="error">Super Admin only.</Alert></Box>;
  }

  const pickedRow = picked ? rows.find((r) => r.uno === picked) : null;
  const busy = phase === 'loading' || phase === 'sending';

  return (
    <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static" elevation={2}>
        <Toolbar variant="dense">
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, fontSize: 16 }}>Device Tracking</Typography>
          <Tooltip title="Re-read the estate">
            <span>
              <IconButton color="inherit" onClick={() => load()} disabled={busy}><RefreshIcon /></IconButton>
            </span>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {phase === 'loading' && <LinearProgress />}

      {/* Summary — all three answers at once, whichever view is open. */}
      {rows.length > 0 && (
        <Box sx={{ px: 1, pt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip size="small" label={`${counts.total} installed`} />
          <Chip size="small" color="success" variant="outlined" label={`${counts.live} online`} />
          <Chip size="small" color="info" variant="outlined" label={`${counts.unassigned} live · unassigned`} />
          <Chip size="small" color="default" variant="outlined" label={`${counts.offline} assigned · offline${offlineDays ? ` ≥ ${offlineDays} d` : ''}`} />
          <Chip size="small" color="warning" variant="outlined" label={`${counts.update} live · to update`} />
        </Box>
      )}

      <Box sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
        <TextField
          select size="small" label="View" value={view}
          onChange={(e) => { setView(e.target.value); setSelected(new Set()); setPicked(null); setResult(null); }}
          sx={{ width: { xs: '100%', sm: 300 } }}
        >
          {VIEWS.map((v) => <MenuItem key={v.key} value={v.key}>{v.label}</MenuItem>)}
        </TextField>
        {view === VIEW_OFFLINE && (
          <TextField
            select size="small" label="Offline for" value={offlineDays}
            onChange={(e) => setOfflineDays(Number(e.target.value))}
            sx={{ width: { xs: '100%', sm: 180 } }}
          >
            {OFFLINE_DAYS.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
          </TextField>
        )}
        <TextField
          select size="small" label="Device type" value={deviceId}
          onChange={(e) => setDeviceId(e.target.value)}
          sx={{ width: { xs: '100%', sm: 240 } }}
        >
          <MenuItem value="">Every type</MenuItem>
          {types.map((t) => <MenuItem key={t.deviceId} value={String(t.deviceId)}>{t.label}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Location" value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
          sx={{ width: { xs: '100%', sm: 240 } }}
        >
          <MenuItem value="">Every location</MenuItem>
          {locations.map((l) => <MenuItem key={l.locationId} value={String(l.locationId)}>{l.label}</MenuItem>)}
        </TextField>
        <TextField
          size="small" placeholder="Search name, number, room, owner…" value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: { xs: '100%', sm: 260 } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
        />
        {chosen.length > 0 && (
          <>
            <Tooltip title="Publish updatecode over MQTT now — only the ticked devices that are online">
              <span>
                <Button
                  variant="contained" color="warning" size="small"
                  startIcon={<SystemUpdateAltIcon />}
                  disabled={!chosenLive.length || busy}
                  onClick={() => askUpdate(chosenLive, 'send')}
                >
                  {`Update code ${chosenLive.length}`}
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Set RequireUpdateCode on the server — each device flashes when it next connects">
              <span>
                <Button
                  variant="outlined" color="warning" size="small"
                  startIcon={<BookmarkAddIcon />}
                  disabled={busy}
                  onClick={() => askUpdate(chosen, 'mark')}
                >
                  {`Mark ${chosen.length}`}
                </Button>
              </span>
            </Tooltip>
          </>
        )}
      </Box>

      {/* Bulk correction for the checked rows — same editor as the detail
          panel, applied to every tick. */}
      {chosen.length > 0 && (
        <Paper variant="outlined" sx={{ mx: 1, mb: 1, p: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 12, color: 'text.secondary', flex: 1 }}>
              {`Correct ${chosen.length} selected device${chosen.length === 1 ? '' : 's'}`}
            </Typography>
            {/* Only where it is the right answer: a silent, assigned device.
                Unassigning a LIVE device is the Device Admin page's job, one at
                a time, with the customer in front of you. */}
            {view === VIEW_OFFLINE && (
              <Button
                size="small" variant="outlined" color="error"
                startIcon={<LinkOffIcon />}
                disabled={busy}
                onClick={openUnassign}
              >
                {`Unassign ${chosen.length}`}
              </Button>
            )}
          </Box>
          <TypeBinEditor
            unos={chosen.map((r) => r.uno)}
            currentTypeId={commonValue(chosen, (r) => r.deviceId)}
            currentBinId={commonValue(chosen, (r) => r.raw.OTABinFileId)}
            types={typeChoices}
            bins={binChoices}
            onSaved={reloadKeeping}
            disabled={busy}
            dense
          />
        </Paper>
      )}

      {error && <Alert severity="error" sx={{ mx: 1, mb: 1 }}>{error}</Alert>}
      {notDeployed && (
        <Alert severity="warning" sx={{ mx: 1, mb: 1 }}>
          The server has no <code>GetDeviceTrackingList</code> yet — deploy the backend
          (<code>api/new/deviceTracking.js</code>) and re-read.
        </Alert>
      )}
      {progress && (
        <Box sx={{ px: 1, pb: 1 }}>
          <Typography sx={{ fontSize: 12, mb: 0.5 }}>
            {progress.label ? `${progress.label} ${progress.total} device${progress.total === 1 ? '' : 's'}…` : `Sending updatecode — ${progress.sent}/${progress.total}`}
          </Typography>
          <LinearProgress variant={progress.label ? 'indeterminate' : 'determinate'} value={progress.total ? (progress.sent / progress.total) * 100 : 0} />
        </Box>
      )}
      {unassignResult && (
        <Alert
          severity={unassignResult.failed.length ? 'warning' : 'success'}
          sx={{ mx: 1, mb: 1 }} onClose={() => setUnassignResult(null)}
        >
          {`Unassigned ${unassignResult.done} device${unassignResult.done === 1 ? '' : 's'} — no owner, no location, no room, no user mappings.`}
          {unassignResult.roomsDropped || unassignResult.locationsDropped
            ? ` Also removed ${unassignResult.roomsDropped} empty room and ${unassignResult.locationsDropped} empty location mapping${unassignResult.locationsDropped === 1 ? '' : 's'}.`
            : ''}
          {unassignResult.failed.length ? ` Failed on ${unassignResult.failed.length}: ${unassignResult.failed.join(', ')}.` : ''}
          {(() => {
            // A device unassigned but not fully reset is named, not averaged away.
            const partial = Object.entries(unassignResult.resets || {}).filter(([, r]) => r && !r.ok).map(([u]) => u);
            return partial.length
              ? ` Reset incomplete on ${partial.join(', ')} — open each in the Message Box and press Unassign → Reset to see which part.`
              : ' Each was reset to a fresh piece.';
          })()}
          {unassignResult.dropFailed ? ` ${unassignResult.dropFailed} room/location mapping${unassignResult.dropFailed === 1 ? '' : 's'} could not be removed.` : ''}
        </Alert>
      )}
      {result && result.mode === 'send' && (
        <Alert severity={result.sent ? 'success' : 'error'} sx={{ mx: 1, mb: 1 }} onClose={() => setResult(null)}>
          {result.sent
            ? `updatecode published to ${result.sent} device${result.sent === 1 ? '' : 's'} over MQTT. Each one downloads its bin and reboots; the flash date moves when it reports back.`
            : 'Nothing was sent — the MQTT broker could not be reached from this browser. Check REACT_APP_MQTT_WS_URL, or use Mark so the devices pick it up on their own.'}
        </Alert>
      )}
      {result && result.mode === 'mark' && (
        <Alert severity={result.failed ? 'warning' : 'success'} sx={{ mx: 1, mb: 1 }} onClose={() => setResult(null)}>
          {`Marked ${result.marked} device${result.marked === 1 ? '' : 's'} on the server${result.failed ? `, ${result.failed} could not be marked` : ''}. Each flashes when it next connects; re-read to see the Marked chip.`}
        </Alert>
      )}

      <Box sx={{ px: 1, pb: 1, flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {pickedRow && (
          <DetailPanel
            row={pickedRow}
            onClose={() => setPicked(null)}
            types={typeChoices}
            bins={binChoices}
            onSaved={reloadKeeping}
            onUpdate={() => askUpdate([pickedRow], 'send')}
            onMark={() => askUpdate([pickedRow], 'mark')}
          />
        )}

        {phase !== 'loading' && rows.length > 0 && (
          <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 0.5 }}>
            {`${visible.length} of ${viewRows.length} in this view`}
            {chosen.length ? ` · ${chosen.length} selected` : ''}
          </Typography>
        )}

        <TableContainer component={Paper} variant="outlined">
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={!allSelected && someSelected}
                    onChange={toggleAll}
                    inputProps={{ 'aria-label': 'Select all shown' }}
                  />
                </TableCell>
                <TableCell>Device</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Room</TableCell>
                <TableCell>Owner</TableCell>
                <TableCell align="right">Users</TableCell>
                <TableCell>Last heard</TableCell>
                {view === VIEW_OFFLINE && <TableCell align="right">Days</TableCell>}
                <TableCell>Bin file</TableCell>
                <TableCell>Firmware</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((r) => {
                const fw = FW_CHIP[r.firmwareState] || FW_CHIP.unknown;
                const open = r.uno === picked;
                return (
                  <TableRow
                    key={r.uno} hover selected={open}
                    onClick={() => setPicked(open ? null : r.uno)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox size="small" checked={selected.has(r.uno)} onChange={() => toggle(r.uno)} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.deviceName}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.uno}</Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{r.deviceTypeName}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{r.locationName}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{r.roomName}</Typography></TableCell>
                    <TableCell><Typography variant="body2">{r.ownerName || '—'}</Typography></TableCell>
                    <TableCell align="right">{r.mappedUsers}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                        {r.lastSeen == null ? 'never' : when(r.lastSeen)}
                      </Typography>
                    </TableCell>
                    {view === VIEW_OFFLINE && (
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{days(r.offlineDays)}</TableCell>
                    )}
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{r.binFile || '—'}</Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip size="small" label={fw.label} color={fw.color} variant={r.firmwareState === STALE ? 'filled' : 'outlined'} />
                      {r.updateMarked && <Chip size="small" label="Marked" sx={{ ml: 0.5 }} />}
                      {/* One device, right here — no ticking. Live rows only:
                          an offline device would just drop the message. */}
                      {r.isLive && (
                        <Tooltip title="Update code — publish over MQTT now">
                          <IconButton
                            size="small" color="warning" sx={{ ml: 0.5 }}
                            disabled={busy}
                            onClick={(e) => { e.stopPropagation(); askUpdate([r], 'send'); }}
                          >
                            <SystemUpdateAltIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {!r.updateMarked && (
                        <Tooltip title="Mark — flashes when it next connects">
                          <IconButton
                            size="small" sx={{ ml: 0.25 }}
                            disabled={busy}
                            onClick={(e) => { e.stopPropagation(); askUpdate([r], 'mark'); }}
                          >
                            <BookmarkAddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {phase === 'ready' && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={11}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      Nothing matches this view and these filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Returning devices to stock — the last look. */}
      <Dialog open={unassignOpen} onClose={() => setUnassignOpen(false)} {...FIT_SM}>
        <DialogTitle>{`Unassign ${chosen.length} device${chosen.length === 1 ? '' : 's'}?`}</DialogTitle>
        <DialogContent dividers>
          <Alert severity="error" sx={{ mb: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>Each device goes back to stock:</Typography>
            <Typography variant="body2">
              owner cleared · location cleared · room cleared · <b>every</b> user&apos;s access to it removed.
            </Typography>
            {/* Unassigned means a FRESH PIECE — core/devices/freshPiece. */}
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              <b>Reset to a fresh piece:</b>
              {' button names and settings (All On / All Off, timers), schedules, two-way links, its steps in anyone’s scenes, the RF links that switch it — all removed; name back to Smartify_<number>. Device type and firmware file are kept.'}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              It disappears from the customer&apos;s app on their next sync. This cannot be undone here —
              re-assigning is done on Device Admin, one device at a time.
            </Typography>
          </Alert>
          {/* What follows above the device: only rooms and locations this run
              leaves EMPTY for a user. Read from the server while the dialog is
              open, so the number is the real one. */}
          {!unassignPlan && (
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 12, mb: 0.5 }}>Checking what each user would have left…</Typography>
              <LinearProgress />
            </Box>
          )}
          {unassignPlan && unassignPlan.error && (
            <Alert severity="error" sx={{ mb: 1 }}>Could not read the users&apos; rooms and locations — nothing will be changed.</Alert>
          )}
          {unassignPlan && !unassignPlan.error && (
            <Alert severity={unassignPlan.roomDrops.length || unassignPlan.locationDrops.length ? 'warning' : 'info'} sx={{ mb: 1 }}>
              <Typography variant="body2">
                {`${unassignPlan.usersAffected} user${unassignPlan.usersAffected === 1 ? '' : 's'} currently see${unassignPlan.usersAffected === 1 ? 's' : ''} these devices.`}
              </Typography>
              <Typography variant="body2">
                {unassignPlan.roomDrops.length || unassignPlan.locationDrops.length
                  ? `This leaves ${unassignPlan.roomDrops.length} room mapping${unassignPlan.roomDrops.length === 1 ? '' : 's'} and ${unassignPlan.locationDrops.length} location mapping${unassignPlan.locationDrops.length === 1 ? '' : 's'} with nothing in them — those will be removed too, so no empty room or site lingers on a phone.`
                  : 'Every affected user still has other devices in these rooms and locations — their room and location access is left as it is.'}
              </Typography>
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            {chosen.map((r) => `${r.deviceName} (${r.uno}) · ${r.locationName} · ${days(r.offlineDays)}`).join('  •  ')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnassignOpen(false)}>Cancel</Button>
          <Button
            variant="contained" color="error" onClick={unassign}
            disabled={!unassignPlan || !!unassignPlan.error || !unassignPlan.steps.length}
          >
            {`Unassign ${chosen.length}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* The last look before hardware reflashes itself. */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} {...FIT_SM}>
        <DialogTitle>
          {mode === 'send'
            ? `Update code — ${targets.length} device${targets.length === 1 ? '' : 's'} now?`
            : `Mark ${targets.length} device${targets.length === 1 ? '' : 's'} for update?`}
        </DialogTitle>
        <DialogContent dividers>
          {mode === 'send' ? (
            <Alert severity="warning" sx={{ mb: 1 }}>
              <Typography variant="caption" sx={{ display: 'block' }}>Published over MQTT to each device, now:</Typography>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, wordBreak: 'break-all' }}>
                {previewPayload(targets[0]?.uno, userId)}
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                Each downloads its bin file and reboots. Nothing is written on the server — a device
                that misses the message is not told again (use Mark for that). Cannot be undone.
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 1 }}>
              <Typography variant="body2">
                Sets <b>RequireUpdateCode = 1</b> (and parks <b>UPDATE CODE</b>) on each device&apos;s row.
                Nothing goes over the air now; each device flashes when it next connects to Wi-Fi or
                polls, and clears the mark itself when the flash succeeds.
              </Typography>
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            {targets.map((r) => `${r.deviceName} (${r.uno})`).join(' · ')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={run}>
            {mode === 'send' ? `Update code ${targets.length}` : `Mark ${targets.length}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
